import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SHARE_PAYMENT_LINK_ROUTE } from '@constants/routes';
import { Channel } from '@domain/channel';
import { useGetActiveChannels } from '@hooks/use-active-channels';
import { useToast } from '@hooks/use-toast/use-toast';

interface useGetSharePaymentLinkProps {
  channel?: Omit<Channel, 'checked' | 'value'>;
  id: string;
  eventId?: string;
  isPass: boolean;
  checkIsChannelActive?: boolean;
}

export const useGetSharePaymentLink = ({
  channel,
  id,
  eventId,
  isPass,
  checkIsChannelActive,
}: useGetSharePaymentLinkProps) => {
  const toast = useToast();
  const { t } = useTranslation();
  const [isShareLinkclick, setIsShareLinkclick] = useState<boolean>(false);

  const { data } = useGetActiveChannels({
    offerId: id,
    enabled: isShareLinkclick && !!checkIsChannelActive,
  });

  const createUrl = () => {
    if (!channel) return;

    let url = SHARE_PAYMENT_LINK_ROUTE.replace(':channel-slug', channel.slug).replace(':offerId', id);
    if (isPass) url += `?eventId=${eventId}`;

    return url;
  };

  const openLink = () => {
    if (!channel) {
      if (toast?.data[0]?.type === 'error') toast?.remove(toast?.data[0]?.id);
      toast.push({
        title: t('eventList:defaultChannelRequired'),
        text: t('eventList:defaultChannelRequiredDescription'),
        type: 'error',
        disableTimeOut: true,
      });
      return;
    }
    window.open(createUrl(), '_blank');
    setIsShareLinkclick(false);
  };

  useEffect(() => {
    if (channel && isShareLinkclick) {
      if (checkIsChannelActive && data) {
        const isChannelActive = data.channels.some((activeChannel) => activeChannel.id === channel.id);
        if (isChannelActive || data.isForAllChannels) {
          openLink();
        } else {
          toast.push({
            title: t('offers:sharePaymentlinkErrorTitle'),
            text: t('offers:sharePaymentlinkErrorBody'),
            type: 'error',
            disableTimeOut: true,
          });
          setIsShareLinkclick(false);
        }
      } else openLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, channel, isShareLinkclick, checkIsChannelActive]);

  return { isShareLinkclick, setIsShareLinkclick };
};
